import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Camera, Search, PawPrint, CheckCircle, Loader2 } from 'lucide-react';
import { apiGet, API_URL, authHeader } from './appApi';
import { CameraScanner } from '../../components/admin/CameraScanner';
import { logger } from '../../utils/logger';

const DOC_TYPES = [
  { value: 'veterinary_health_certificate', label: 'Vet Health Certificate' },
  { value: 'vaccination_records',           label: 'Vaccination Records' },
  { value: 'microchip_documentation',       label: 'Microchip' },
  { value: 'pet_passport',                  label: 'Pet Passport' },
  { value: 'import_permit',                 label: 'Import Permit' },
  { value: 'rabies_titer_test',             label: 'Rabies Titer Test' },
  { value: 'pet_photos',                    label: 'Pet Photos' },
  { value: 'owner_passport',                label: 'Owner Passport' },
  { value: 'flight_booking',                label: 'Flight Booking' },
  { value: 'boarding_records',              label: 'Boarding Records' },
  { value: 'insurance',                     label: 'Insurance' },
  { value: 'breed_certificate',             label: 'Breed Certificate' },
  { value: 'other',                         label: 'Other' },
];

export const AppScan = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedPetId = params.get('pet_id');

  const [step, setStep] = useState('pick_pet'); // pick_pet | type | scanner | uploading | done
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedPet, setSelectedPet] = useState(null);
  const [docType, setDocType] = useState('other');
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [savedDoc, setSavedDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await apiGet('/api/admin/pets');
        setPets(list);
        if (preselectedPetId) {
          const p = list.find((x) => x.id === preselectedPetId);
          if (p) { setSelectedPet(p); setStep('type'); }
        }
      } catch (err) {
        logger.error('AppScan pets fetch', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [preselectedPetId]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return pets;
    return pets.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.client_name || '').toLowerCase().includes(q) ||
      (p.breed || '').toLowerCase().includes(q)
    );
  }, [pets, query]);

  const uploadPdf = async (file) => {
    setStep('uploading');
    setUploadPct(0);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', docType);
      form.append('tags', 'mobile-scan');

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/admin/pets/${selectedPet.id}/documents`);
        const headers = authHeader();
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
          ? resolve(JSON.parse(xhr.responseText))
          : reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
      });
      setSavedDoc(result);
      setStep('done');
    } catch (err) {
      logger.error('Mobile scan upload failed', err);
      setUploadError(err.message || 'Upload failed');
      setStep('type'); // allow retry
    }
  };

  const startOver = () => {
    setSavedDoc(null);
    setSelectedPet(null);
    setDocType('other');
    setStep('pick_pet');
    setUploadError(null);
    setUploadPct(0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      {step !== 'scanner' && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10"
             style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}>
          <button
            onClick={() => step === 'type' ? setStep('pick_pet') : navigate(-1)}
            data-testid="scan-back"
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Scan Document</h1>
            <p className="text-xs text-gray-500">
              {step === 'pick_pet' && 'Choose a pet folder'}
              {step === 'type' && selectedPet && `${selectedPet.name} · Choose document type`}
              {step === 'uploading' && 'Uploading scanned PDF…'}
              {step === 'done' && 'Saved to pet folder'}
            </p>
          </div>
        </div>
      )}

      {/* Step 1 — pick pet */}
      {step === 'pick_pet' && (
        <div className="p-4" data-testid="scan-pet-picker">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pets or clients"
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-white rounded-xl border border-gray-200"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <PawPrint className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">{query ? 'No matches' : 'No pets yet — add one in Admin'}</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => { setSelectedPet(p); setStep('type'); }}
                    data-testid={`scan-pick-pet-${p.id}`}
                    className="w-full bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 active:bg-gray-50 text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center">
                      <PawPrint className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {p.species}{p.breed ? ` · ${p.breed}` : ''} · {p.client_name}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Step 2 — document type */}
      {step === 'type' && selectedPet && (
        <div className="p-4" data-testid="scan-type-picker">
          <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center">
              <PawPrint className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 text-sm">{selectedPet.name}</p>
              <p className="text-xs text-gray-500">{selectedPet.client_name}</p>
            </div>
          </div>

          {uploadError && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="scan-error">
              {uploadError}
            </div>
          )}

          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Document Type</p>
          <div className="grid grid-cols-2 gap-2 mb-5">
            {DOC_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setDocType(t.value)}
                data-testid={`scan-doctype-${t.value}`}
                className={`p-3 rounded-lg border text-left text-sm font-medium active:scale-95 transition ${
                  docType === t.value
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white border-gray-200 text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setStep('scanner')}
            data-testid="scan-open-camera"
            className="w-full py-4 rounded-xl bg-teal-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-95"
          >
            <Camera className="w-5 h-5" /> Open Camera
          </button>
        </div>
      )}

      {/* Step 3 — scanner */}
      <CameraScanner
        open={step === 'scanner'}
        defaultName={selectedPet ? `${selectedPet.name.replace(/\s+/g, '-').toLowerCase()}-scan` : 'scan'}
        onClose={() => setStep('type')}
        onCapture={(file) => uploadPdf(file)}
      />

      {/* Step 4 — uploading */}
      {step === 'uploading' && (
        <div className="p-6 text-center" data-testid="scan-uploading">
          <Loader2 className="w-10 h-10 mx-auto text-teal-600 animate-spin mb-3" />
          <p className="text-sm font-semibold text-gray-900">Uploading PDF…</p>
          <p className="text-xs text-gray-500 mb-4">{uploadPct}% complete</p>
          <div className="max-w-sm mx-auto h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-600 transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Step 5 — done */}
      {step === 'done' && savedDoc && (
        <div className="p-6 text-center" data-testid="scan-done">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center mb-3">
            <CheckCircle className="w-8 h-8" />
          </div>
          <p className="text-lg font-bold text-gray-900">Saved to {selectedPet?.name}'s folder</p>
          <p className="text-sm text-gray-500 mb-6 truncate">{savedDoc.file_name}</p>

          <div className="space-y-2 max-w-sm mx-auto">
            <button
              onClick={startOver}
              data-testid="scan-another"
              className="w-full py-3 rounded-xl bg-teal-600 text-white font-semibold active:scale-95"
            >
              Scan Another
            </button>
            <button
              onClick={() => navigate(`/admin/pets/${selectedPet.id}`)}
              className="w-full py-3 rounded-xl border border-gray-200 bg-white text-gray-800 font-semibold"
              data-testid="scan-view-folder"
            >
              View Pet Folder
            </button>
            <button
              onClick={() => navigate('/app/home')}
              className="w-full py-3 text-sm text-gray-500"
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppScan;
